import { ActivitySpinnerWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ReactResizeDetector } from '~src/commonInfra/components/ReactResizeDetector';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { TOTAL_CARD_WIDTH } from '~src/spacesPlatform/components/card/CardViewStyles';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerEmptyState, ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { Messages } from '../../../codegen/Localize';
import { ChannelStyles } from '../../../home/styles/ChannelStyles';
import { ChannelTypes } from '../../../home/types/ChannelTypes';
import { ShowMoreRecommendationsAction } from '../../actions/ShowMoreRecommendationsAction';
import { RecommendationsShowMoreState } from '../../state/RecommendationsState';
import { ConnectedRecommendationsChannel } from '../ConnectedRecommendationsChannel';

export interface RecommendationsSpaceStateProps {
  allRecsById: readonly string[];
  readonly isFetching: boolean;
  readonly showMoreState: RecommendationsShowMoreState;
}

export interface RecommendationSpaceDispatchProps {
  readonly showMoreRecommendations: (maxCardsPerRow: number, channelType: ChannelTypes) => ShowMoreRecommendationsAction;
}

export type RecommendationsSpaceProps = RecommendationsSpaceStateProps & RecommendationSpaceDispatchProps;

export interface RecommendationsSpaceLocalState {
  maxCardsPerRow: number;
}

export class RecommendationsSpace extends React.PureComponent<RecommendationsSpaceProps, RecommendationsSpaceLocalState> {
  private recommendationsSpaceContainerRef: React.RefObject<HTMLDivElement>;
  private recommendationSpaceRef: React.RefObject<HTMLDivElement>;
  private debounceMaxCardsPerRow: () => void;

  constructor(props: RecommendationsSpaceProps) {
    super(props);
    this.state = {
      maxCardsPerRow: 0
    };
    this.recommendationsSpaceContainerRef = React.createRef();
    this.recommendationSpaceRef = React.createRef();
    this.debounceMaxCardsPerRow = _.debounce(() => { this.setState({ maxCardsPerRow: this.calculateMaxCardsPerRow()}); }, 50);
  }

  public componentDidMount() {
    this.setState({ maxCardsPerRow: this.calculateMaxCardsPerRow()});
  }

  public componentDidUpdate(prevProps: RecommendationsSpaceProps, prevState: RecommendationsSpaceLocalState) {
    const newMaxCardsPerRow = this.calculateMaxCardsPerRow();
    if (prevState.maxCardsPerRow !== newMaxCardsPerRow) {
      this.setState({ maxCardsPerRow: newMaxCardsPerRow });
    }
  }

  public render(): JSX.Element {

    let recommendationsSpaceContent;
    if (!this.props.isFetching && this.props.allRecsById.length > 0) {
      recommendationsSpaceContent = [
          this.renderSimilarUsersChannel(),
          this.renderPopularChannel()
        ];
    } else if (this.props.isFetching) {
      recommendationsSpaceContent = this.renderSpinner();
    } else {
      recommendationsSpaceContent = <ExplorerEmptyState {...this.emptyStateProps()}/>;
    }
    return(
      <div className={SpaceContainerStyles.SpaceContainer} ref={this.recommendationsSpaceContainerRef}>
        <div className={SpaceContainerStyles.MastheadArea}>
            <SpaceHeaderContent headerText={Messages.vizRecommendationsTitle()} />
        </div>
        <div className={SpaceContainerStyles.SubplaceArea + ' ' + SpaceContainerStyles.SubplaceAreaCenterMargins} ref={this.recommendationSpaceRef}>
          {recommendationsSpaceContent}
        </div>
        <ReactResizeDetector
          targetRef={this.recommendationsSpaceContainerRef}
          handleWidth
          handleHeight
          skipOnMount={true}
          onResize={this.debounceMaxCardsPerRow} />
      </div>
    );
  }

  private renderSimilarUsersChannel(): JSX.Element {
    return (
      <ConnectedRecommendationsChannel
        key={ChannelTypes.recsSimilarUsers}
        showMoreCards={this.props.showMoreRecommendations}
        numCardsToShow={this.props.showMoreState.similarUsers}
        title={Messages.vizRecommendationsSimiliarUsersExplanationChannelTitle()}
        maxCardsPerRow={this.state.maxCardsPerRow}
        channelType={ChannelTypes.recsSimilarUsers}
        headerIconPath="for_you_large_18px.svg"
      />
    );
  }

  public renderPopularChannel(): JSX.Element {
    return (
      <ConnectedRecommendationsChannel
        key={ChannelTypes.recsPopular}
        showMoreCards={this.props.showMoreRecommendations}
        numCardsToShow={this.props.showMoreState.popular}
        title={Messages.vizRecommendationsPopularExplanationChannelTitle()}
        maxCardsPerRow={this.state.maxCardsPerRow}
        channelType={ChannelTypes.recsPopular}
        headerIconPath="trending_large_18px.svg"
      />
    );
  }

  private renderSpinner(): JSX.Element {
    return (
      <div className={SpaceContainerStyles.SpaceCenteredContent}>
        <div className={ExplorerStyles.SpinnerContainer}>
          <ActivitySpinnerWidget testId='recommendations-space-spinner' />
        </div>
      </div>
    );
  }

  /**
   * Calculates how many cards fit into the width of the channel. Used to help limit channels to N amount of rows.
   */
   private calculateMaxCardsPerRow(): number {
    const channelWidth = this.recommendationSpaceRef.current.getBoundingClientRect().width - ChannelStyles.horizontalMarginBetweenCards - ChannelStyles.cardBorder;
    const cardWidth = TOTAL_CARD_WIDTH + ChannelStyles.horizontalMarginBetweenCards + ChannelStyles.cardBorder;
    const maxCardsPerRow = Math.floor(channelWidth / cardWidth);
    return maxCardsPerRow;
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      iconUrl: imgPath('spaces_empty_recommendation.svg'),
      primaryMessage: Messages.vizRecommendationsEmptyStatePrimary(),
      secondaryMessage: Messages.vizRecommendationsEmptyStateSecondary()
    };
  }
}
